import { useState, useEffect } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Clipboard } from "lucide-react";
import PrimaryFunctions from "./PrimaryFunctions.js";

function DevHome() {
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [email, setEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [role, setRole] = useState("Developer");
  // mousePosition state removed as the animated background is removed
  // const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 }); 

  // useEffect removed as it was only for the animated background
  // useEffect(() => {
  //   const handleMouseMove = (e) => {
  //     setMousePosition({ x: e.clientX, y: e.clientY });
  //   };
  //   window.addEventListener('mousemove', handleMouseMove);
  //   return () => window.removeEventListener('mousemove', handleMouseMove);
  // }, []);

  const codeSnippet = `from desync_search.core import DesyncClient

user_api_key = "YOUR_DESYNC_KEY"
client = DesyncClient(user_api_key)`;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(codeSnippet);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(2000));
  };

  const handleCopyCommand = () => {
    navigator.clipboard.writeText("pip install desync_search");
    setCopiedCommand(true);
    setTimeout(() => setCopiedCommand(2000));
  };

  return (
    <section className="text-white min-h-screen flex flex-col items-center">
      {/* Animated background effect and related state/useEffect removed for stylistic consistency */}
      {/* Hero Section */}
      <div className="max-w-7xl w-full flex flex-col md:flex-row items-center justify-between space-y-10 md:space-y-0 py-12 mt-[11%] px-6 relative">
        {/* Left - Heading & Buttons */}
        <div className="md:w-[60%] text-center md:text-left">
          <div className="inline-block mb-4 animate-slideInLeft">
            <span className="px-4 py-2 rounded-full glass text-sm font-medium text-[#25BC82]"> {/* neon-border removed */}
              💻 Developer Portal
            </span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black animate-fadeInUp leading-tight">
            Data Extraction
            <br />
            <span className="gradient-text">Made Simple</span>
          </h1>
          <p className="text-gray-400 text-lg mt-6 animate-fadeInUp delay-200 max-w-lg">
            A software service built to extract web data - bypassing bot detection and delivering clean results to Excel, Sheets, dashboards, or your API
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center md:justify-start animate-fadeInUp delay-400">
            <a
              href="https://beta.desync.ai/register"
              target="_blank"
              rel="noreferrer"
              className="group relative px-8 py-4 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-lg font-semibold text-white overflow-hidden transition-all duration-300 hover:scale-105" // animate-pulse-glow removed
            >
              <span className="relative z-10 flex items-center gap-2">
                <span>Get An API Key</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </span>
            </a>
            <a
              href="/home"
              className="px-8 py-4 glass rounded-lg font-semibold hover:bg-white/10 transition-all duration-300" // Styled to match Home component's secondary button
            >
              For Non-Developers
            </a>
          </div>
        </div>

        {/* Right - Code Boxes */}
        <div className="md:w-[40%] w-full animate-fadeInUp delay-200">
          <div className="relative group">
            {/* Glow/blur effect removed from here */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl opacity-20 blur-2xl group-hover:opacity-30 transition-opacity duration-500" /> */}
            <div className="relative glass rounded-2xl shadow-xl p-6 space-y-4">
              <div className="flex items-center justify-between glass rounded-xl p-4">
                <div className="flex items-center space-x-2">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/Python-logo-notext.svg/1869px-Python-logo-notext.svg.png"
                    alt="Python Logo"
                    className="h-6 w-auto"
                  />
                  <span className="text-gray-300 text-sm md:text-base break-all">
                    pip install desync_search
                  </span>
                </div>
                <button
                  className="text-gray-400 hover:text-white transition p-1 rounded"
                  onClick={handleCopyCommand}
                >
                  {copiedCommand ? "✅" : <Clipboard size={14} />}
                </button>
              </div>

              <div className="glass rounded-xl p-4 relative overflow-x-auto">
            <SyntaxHighlighter
              language="python"
              style={{
                ...oneDark,
                "pre[class*='language-']": {
                  backgroundColor: "transparent",
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                },
                "code[class*='language-']": { backgroundColor: "transparent" },
              }}
              showLineNumbers={true}
              lineNumberStyle={{ color: "#888", fontSize: "12px", paddingRight: "10px" }}
              className="rounded-md text-sm"
            >
              {codeSnippet}
            </SyntaxHighlighter>
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-white transition p-1 rounded"
              onClick={handleCopyCode}
            >
              {copiedCode ? "✅" : <Clipboard size={14} />}
            </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="max-w-7xl w-full px-6 py-20"> {/* Removed bg-gradient from here */}
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="gradient-text">Current Features</span>
          </h2>
          <p className="text-gray-400 text-lg">The easiest way to automate web workflows without hassle</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="group relative">
            {/* Glow/blur effect removed from here */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500" /> */}
            <div className="relative glass rounded-2xl p-8 h-full hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-xl flex items-center justify-center mb-6"> {/* animate-float removed */}
                <span className="text-3xl">🕵️‍♂️</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Stealth Search</h3>
              <p className="text-gray-400">"Human-like" visits to bypass CAPTCHAs</p>
            </div>
          </div>

          <div className="group relative">
            {/* Glow/blur effect removed from here */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500" /> */}
            <div className="relative glass rounded-2xl p-8 h-full hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-xl flex items-center justify-center mb-6"> {/* animate-float delay-200 removed */}
                <span className="text-3xl">⚙️</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Dev Friendly</h3>
              <p className="text-gray-400">3 lines of Python</p>
            </div>
          </div>

          <div className="group relative">
            {/* Glow/blur effect removed from here */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500" /> */}
            <div className="relative glass rounded-2xl p-8 h-full hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-xl flex items-center justify-center mb-6"> {/* animate-float delay-400 removed */}
                <span className="text-3xl">🛡️</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Reliable</h3>
              <p className="text-gray-400">5,000,000+ pages extracted</p>
            </div>
          </div>

          <div className="group relative">
            {/* Glow/blur effect removed from here */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500" /> */}
            <div className="relative glass rounded-2xl p-8 h-full hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-xl flex items-center justify-center mb-6"> {/* animate-float removed */}
                <span className="text-3xl">📈</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Scalable</h3>
              <p className="text-gray-400">50,000 concurrent operations</p>
            </div>
          </div>

          <div className="group relative">
            {/* Glow/blur effect removed from here */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500" /> */}
            <div className="relative glass rounded-2xl p-8 h-full hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-xl flex items-center justify-center mb-6"> {/* animate-float delay-200 removed */}
                <span className="text-3xl">🔍</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Transparent</h3>
              <p className="text-gray-400">No hidden fees</p>
            </div>
          </div>

          <div className="group relative">
            {/* Glow/blur effect removed from here */}
            {/* <div className="absolute inset-0 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500" /> */}
            <div className="relative glass rounded-2xl p-8 h-full hover:scale-105 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-[#095070] to-[#25BC82] rounded-xl flex items-center justify-center mb-6"> {/* animate-float delay-400 removed */}
                <span className="text-3xl">🧠</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">Realistic</h3>
              <p className="text-gray-400">Render full JavaScript</p>
            </div>
          </div>
        </div>
      </section>

      {/* Primary Functions */}
      <div className="relative z-10">
        <PrimaryFunctions />
      </div>

      {/* Comparison Table */}
      <section className="max-w-7xl w-full px-4 md:px-6 py-10 md:py-16">
        <h2 className="text-3xl md:text-4xl mb-2 text-white">
          How Desync Compares to Alternatives
        </h2>
        <p className="text-gray-400 mb-8">See what Desync offers compared to everybody else</p>

        {/* No horizontal scroll needed because we hide columns instead */}
        <div className="w-full">
          <table className="w-full border-collapse text-white">
            {/* Table Header */}
            <thead>
              <tr className="border-b border-gray-700">
                <th className="py-4 w-1/6"></th>

                {/* Desync AI */}
                <th className="py-4 w-1/6 font-semibold text-center">
                  <img
                    src="/logo.svg"
                    alt="Desync AI"
                    className="mx-auto h-8 md:h-10 w-auto"
                  />
                </th>

                {/* Browser Base */}
                <th className="py-4 w-1/6 font-semibold text-center hidden md:table-cell">
                  <img
                    src="/images/browser_base.svg"
                    alt="Browser Base"
                    className="mx-auto h-8 md:h-10 w-auto object-contain"
                  />
                </th>

                {/* Browser Use */}
                <th className="py-4 w-1/6 font-semibold text-center hidden lg:table-cell">
                  <img
                    src="/images/browser_use.png"
                    alt="Browser Use"
                    className="mx-auto h-8 md:h-10 w-auto object-contain"
                  />
                </th>

                {/* Scraper API */}
                <th className="py-4 w-1/6 font-semibold text-center hidden xl:table-cell">
                  <img
                    src="/images/scraperapi.png"
                    alt="Scraper API"
                    className="mx-auto h-8 md:h-10 w-auto object-contain rounded-sm"
                  />
                </th>

                {/* Apify */}
                <th className="py-4 w-1/6 font-semibold text-center hidden xl:table-cell">
                  <img
                    src="/images/apify.png"
                    alt="Apify"
                    className="mx-auto h-10 w-auto object-contain"
                  />
                </th>
              </tr>
            </thead>

            <tbody>
              {/* (1) Minimum Spend */}
              <tr className="border-b border-gray-700">
                <td className="p-3 md:p-4 font-semibold">Minimum spend</td>
                {/* Desync AI */}
                <td className="p-3 md:p-4 bg-gray-800 text-center">
                  <div className="uppercase text-xs mb-1">Starting from</div>
                  <div className="text-xl md:text-3xl font-bold leading-tight">$1</div>
                  <div className="text-gray-300 text-xs">per 10k credits</div>
                </td>

                {/* Browser Base */}
                <td className="p-3 md:p-4 text-center hidden md:table-cell">
                  <div className="uppercase text-xs mb-1">Starting from</div>
                  <div className="text-xl font-bold">$39</div>
                  <div className="text-gray-300 text-xs">USD/mo</div>
                </td>

                {/* Browser Use */}
                <td className="p-3 md:p-4 text-center hidden lg:table-cell">
                  <div className="uppercase text-xs mb-1">Starting from</div>
                  <div className="text-xl font-bold">$30</div>
                  <div className="text-gray-300 text-xs">USD/mo</div>
                </td>

                {/* Scraper API */}
                <td className="p-3 md:p-4 text-center hidden xl:table-cell">
                  <div className="uppercase text-xs mb-1">Starting from</div>
                  <div className="text-xl font-bold">$49</div>
                  <div className="text-gray-300 text-xs">USD/mo</div>
                </td>

                {/* Apify */}
                <td className="p-3 md:p-4 text-center hidden xl:table-cell">
                  <div className="uppercase text-xs mb-1">Starting from</div>
                  <div className="text-xl font-bold">$39</div>
                  <div className="text-gray-300 text-xs">USD/mo</div>
                </td>
              </tr>

              {/* (2) Pay as you go */}
              <tr className="border-b border-gray-700">
                <td className="p-3 md:p-4">Pay as you go</td>
                <td className="p-3 md:p-4 bg-gray-800 text-center">
                  <span className="text-green-400 text-xl md:text-2xl font-bold">
                    ✓
                  </span>
                </td>

                <td className="p-3 md:p-4 text-center hidden md:table-cell">
                  <span className="text-red-500 text-xl md:text-2xl font-bold">
                    ✕
                  </span>
                </td>

                <td className="p-3 md:p-4 text-center hidden lg:table-cell">
                  <span className="text-red-500 text-xl md:text-2xl font-bold">
                    ✕
                  </span>
                </td>

                <td className="p-3 md:p-4 text-center hidden xl:table-cell">
                  <span className="text-red-500 text-xl md:text-2xl font-bold">
                    ✕
                  </span>
                </td>

                <td className="p-3 md:p-4 text-center hidden xl:table-cell">
                  <span className="text-red-500 text-xl md:text-2xl font-bold">
                    ✕
                  </span>
                </td>
              </tr>

              {/* (3) No hidden/complex fees */}
              <tr className="border-b border-gray-700">
                <td className="p-3 md:p-4">No hidden/complex fees</td>
                <td className="p-3 md:p-4 bg-gray-800 text-center">
                  <span className="text-green-400 text-xl md:text-2xl font-bold">
                    ✓
                  </span>
                </td>

                <td className="p-3 md:p-4 text-center hidden md:table-cell">
                  <span className="text-red-500 text-xl md:text-2xl font-bold">
                    ✕
                  </span>
                </td>

                <td className="p-3 md:p-4 text-center hidden lg:table-cell">
                  <span className="text-red-500 text-xl md:text-2xl font-bold">
                    ✕
                  </span>
                </td>

                <td className="p-3 md:p-4 text-center hidden xl:table-cell">
                <span className="text-green-400 text-xl md:text-2xl font-bold">
                    ✓
                  </span>
                </td>

                <td className="p-3 md:p-4 text-center hidden xl:table-cell">
                  <span className="text-red-500 text-xl md:text-2xl font-bold">
                    ✕
                  </span>
                </td>
              </tr>

              {/* (4) Concurrent searches */}
              <tr>
                <td className="p-3 md:p-4">Max concurrency (based on plan)</td>
                <td className="p-3 md:p-4 bg-gray-800 text-center text-base md:text-lg font-semibold">
                  50,000
                </td>

                <td className="p-3 md:p-4 text-center font-semibold hidden md:table-cell">
                  3-50
                </td>

                <td className="p-3 md:p-4 text-center font-semibold hidden xl:table-cell">
                  1-50
                </td>

                <td className="p-3 md:p-4 text-center font-semibold hidden xl:table-cell">
                  20-200
                </td>

                <td className="p-3 md:p-4 text-center font-semibold hidden xl:table-cell">
                  25-256
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

export default DevHome;